import { toZonedTime } from 'date-fns-tz'

import {
  ManagerialBooking,
  UnavailableSlot,
  User,
} from '@/api/reservations/get-reservation-details'
import { BadgeColorKeys, BadgeVariantKeys } from '@/components/ui/badge/badge.types'
import i18n from '@/i18n'
import { UserRole } from '@/models'
import { formatDollars } from '@/utils/currency'

import { ReservationDetailsTab, ReservationDetailsTabsEnum } from './reservation-details.constants'

const dayDateFormatter = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  month: 'short',
  weekday: 'short',
  year: 'numeric',
})

const timeFormatter = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
})

const monthDayFormatter = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  month: 'short',
})

const monthDayYearFormatter = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

const dayFormatter = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
})

// Mirrors Intl.DateTimeFormat.prototype.formatRange's collapsing behavior (unavailable under
// this project's `target`/`lib`): same year + month collapses to "May 30 – 31, 2026", same year
// only to "May 30 – Jun 2, 2026", otherwise "Dec 30, 2025 – Jan 2, 2026".
const formatMultidayRange = (start: Date, end: Date) => {
  const sameYear = start.getFullYear() === end.getFullYear()

  if (!sameYear) {
    return `${monthDayYearFormatter.format(start)} – ${monthDayYearFormatter.format(end)}`
  }

  const sameMonth = start.getMonth() === end.getMonth()
  const endLabel = sameMonth ? dayFormatter.format(end) : monthDayFormatter.format(end)

  return `${monthDayFormatter.format(start)} – ${endLabel}, ${end.getFullYear()}`
}

type PaymentDetails = {
  amount: string | null
  label: string | null
  pill: {
    color: BadgeColorKeys
    text: string
    variant?: BadgeVariantKeys
    refunded?: boolean
  } | null
  strike: boolean
}

export const getPaymentInfo = ({
  isPaidAmenity,
  chargeAmount,
  refundAmount,
  isCancelled,
}: {
  isPaidAmenity?: boolean | null
  chargeAmount?: number | null
  refundAmount?: number | null
  isCancelled?: boolean | null
}): PaymentDetails => {
  const paid = chargeAmount ?? 0
  const refunded = refundAmount ?? 0

  if (!isPaidAmenity || paid === 0) {
    return {
      amount: null,
      label: null,
      pill: {
        color: 'green',
        text: i18n.t('reservations:details.header.free'),
      },
      strike: false,
    }
  }

  if (isCancelled && refunded === paid) {
    return {
      amount: formatDollars(paid, { zeroPrice: true }),
      label: i18n.t('reservations:details.header.originallyPaid'),
      pill: {
        color: 'red',
        refunded: true,
        text: i18n.t('reservations:details.header.fullRefund'),
        variant: 'light',
      },
      strike: true,
    }
  }

  if (refunded > 0 && refunded < paid) {
    return {
      amount: formatDollars(paid - refunded, { zeroPrice: true }),
      label: i18n.t('reservations:details.header.netPaid'),
      pill: {
        color: 'red',
        refunded: true,
        text: i18n.t('reservations:details.header.partialRefund'),
        variant: 'light',
      },
      strike: false,
    }
  }

  return {
    amount: formatDollars(paid, { zeroPrice: true }),
    label: i18n.t('reservations:details.header.paid'),
    pill: null,
    strike: false,
  }
}

export const getReservationInfo = ({
  date,
  endDate,
  startTime,
  endTime,
  timezone,
}: {
  date?: number | null
  endDate?: number | null
  startTime?: number | null
  endTime?: number | null
  timezone: string
}) => {
  if (date == null || startTime == null || endTime == null) return ''

  const start = toZonedTime(new Date(date + startTime * 60_000), timezone)
  const end = toZonedTime(new Date((endDate ?? date) + endTime * 60_000), timezone)

  // Multiday amenities are booked in whole-day increments, so they always carry
  // startTime/endTime === 0 and are rendered as a bare date range (no times).
  const isMultiday = startTime === 0 && endTime === 0

  if (isMultiday) {
    return formatMultidayRange(start, end)
  }

  return `${dayDateFormatter.format(start)}, ${timeFormatter.format(start)} – ${timeFormatter.format(end)}`
}

type ReservationTabCountParams = {
  tab: ReservationDetailsTab
  customQuestionsCount?: number
  reservationTransactionsCount?: number
  reservationNotesCount?: number
  documentsCount?: number
}

export const getReservationTabCount = ({
  tab,
  customQuestionsCount,
  reservationTransactionsCount,
  reservationNotesCount,
  documentsCount,
}: ReservationTabCountParams) => {
  switch (tab) {
    case ReservationDetailsTabsEnum.enum.payments:
      return reservationTransactionsCount ?? 0

    case ReservationDetailsTabsEnum.enum['custom-questions']:
      return customQuestionsCount ?? 0

    case ReservationDetailsTabsEnum.enum.notes:
      return reservationNotesCount ?? 0

    case ReservationDetailsTabsEnum.enum.documents:
      return documentsCount ?? 0

    default:
      return undefined
  }
}

export const getUserRoleTranslation = (userRole?: UserRole) => {
  if (!userRole) {
    return ''
  }

  const roleTranslationKeys: Partial<Record<UserRole, string>> = {
    Administrator: i18n.t('reservations:details.details.roles.administrator'),
    Client: i18n.t('reservations:details.details.roles.client'),
    ParkingStaff: i18n.t('reservations:details.details.roles.parkingStaff'),
    PropertyManager: i18n.t('reservations:details.details.roles.propertyManager'),
    ShiftManager: i18n.t('reservations:details.details.roles.shiftManager'),
    Staff: i18n.t('reservations:details.details.roles.staff'),
    Trainer: i18n.t('reservations:details.details.roles.trainer'),
    Valet: i18n.t('reservations:details.details.roles.valet'),
  } as const

  const role = roleTranslationKeys[userRole]

  return role ?? ''
}

export const getUserInfo = (user?: User | null) => {
  if (!user) {
    return undefined
  }

  const role = getUserRoleTranslation(user.role)

  return `${user.firstName} ${user.lastName}, ${user.apartment ? `${user.apartment}, ` : ''} ${role}`
}

const getDateWithTime = (date: number, time?: number | null, endOfDay = false) => {
  const result = new Date(date)

  if (time == null) {
    result.setHours(endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0)

    return result.getTime()
  }

  result.setHours(Math.floor(time / 60), time % 60, 0, 0)

  return result.getTime()
}

export const getTimeBlockedLocationIds = ({
  managerialBooking,
  unavailableSlots,
  date,
  endDate,
  startTime,
  endTime,
}: {
  managerialBooking?: ManagerialBooking[] | null
  unavailableSlots?: UnavailableSlot[] | null
  date?: number
  endDate?: number | null
  startTime?: number | null
  endTime?: number | null
}) => {
  if (!date) {
    return []
  }

  const reservationStartDate = getDateWithTime(date, startTime)

  const reservationEndDate = getDateWithTime(endDate ?? date, endTime, true)

  const blockedSlots = [
    ...(managerialBooking ?? []).map(({ startDate, endDate, locationId }) => ({
      endDate: getDateWithTime(endDate, null, true),
      locationId,
      startDate: getDateWithTime(startDate),
    })),

    ...(unavailableSlots ?? []).map(({ date, endDate, startTime, endTime, locationId }) => ({
      endDate: getDateWithTime(endDate ?? date, endTime, true),
      locationId,
      startDate: getDateWithTime(date, startTime),
    })),
  ]

  return [
    ...new Set(
      blockedSlots
        .filter(
          ({ startDate, endDate }) =>
            reservationStartDate < endDate && reservationEndDate > startDate,
        )
        .map(({ locationId }) => locationId),
    ),
  ]
}
